<script lang="ts" setup>
import {
  defineOgImageComponent,
  onMounted,
  ref,
  useRoute,
  useDialog,
  useAuth,
  useUser,
  useCategories,
  callOnce,
  useSupabase,
  useRouter,
  computed,
  usePlaces,
} from "#imports";
import { until, useWindowSize } from "@vueuse/core";
import MPCheckEmail from "./components/dialogs/MPCheckEmail.vue";
import MPMessage from "./components/widgets/MPMessage.vue";
import MPAccountDraw from "./components/layouts/MPAccountDraw.vue";
import MPDialog from "./components/dialogs/MPDialog.vue";
import MPToast from "./components/widgets/MPToast.vue";
import MPConfetti from "./components/widgets/MPConfetti.vue";
import MPImg from "./components/widgets/MPImg.vue";
import { type UserTier } from "./stores/user.ts";

const route = useRoute();
const router = useRouter();
const authStore = useAuth();
const userStore = useUser();
const placeStore = usePlaces();
const categoryStore = useCategories();
const supabase = useSupabase();

const isOAuthing = computed(() => !!route.query.code);

await callOnce(async () => {
  // this only ever runs in SSR
  // prefetch data required for basically every page
  await categoryStore.fetchCategories(null);
  await placeStore.fetchCountries(null);
  await placeStore.fetchRegions(null);

  if (!isOAuthing.value) {
    if (!authStore.isLoggedIn) {
      await supabase.auth.getClaims();
      await supabase.auth.getUser();
    }
    if (authStore.isLoggedIn) {
      await userStore.fetchUserInfo();
    }
  }
});

// hack to mitigate supabase user problem
onMounted(async () => {
  if (authStore.isLoggedIn) {
    await userStore.fetchUserInfo();
  }
});

defineOgImageComponent(
  "Default",
  {
    title: "Discover your next adventure",
    description: "Explore curated lists from local experts and global creators",
  },
  {
    url: "/media/videos/5-poster.webp",
  },
);

// Confetti Subscription Success
const { width: viewPortWidth, height: viewPortHeight } = useWindowSize();
const { openDialog } = useDialog();
const subscriptionSuccessful = ref(false);
const showConfetti = ref(false);

const confettiColors = [
  "var(--color-green-400)",
  "var(--color-rose-400)",
  "var(--color-indigo-400)",
  "var(--color-orange-400)",
  "var(--color-green-200)",
  "var(--color-rose-200)",
  "var(--color-indigo-200)",
  "var(--color-orange-200)",
  "var(--color-green-800)",
  "var(--color-rose-100)",
  "var(--color-indigo-700)",
  "var(--color-orange-300)",
];

onMounted(async () => {
  if (isOAuthing.value) {
    await until(() => authStore.isLoggedIn).toBeTruthy();
    router.replace({ query: {} });
  }

  if (route.query.payment === "success") {
    openDialog(MPCheckEmail, {
      tier: route.query.tier as UserTier,
    });
    showConfetti.value = true;
    setTimeout(() => {
      subscriptionSuccessful.value = false;
    }, 3000);
  }
});
</script>

<template>
  <div>
    <NuxtLoadingIndicator />
    <NuxtLayout>
      <div v-if="isOAuthing" class="logging-in">
        <MPImg src="/media/images/authenticating-graphic.svg" />
        <p>Authenticating</p>
      </div>
    </NuxtLayout>

    <MPMessage
      v-if="subscriptionSuccessful"
      version="success"
      class="subscription-success"
    >
      <h4>Payment successfully processed.</h4>
      <p>Thank you for your support!</p>
    </MPMessage>
    <MPAccountDraw />
    <MPDialog />
    <MPToast />
    <MPConfetti
      v-if="showConfetti"
      :colors="confettiColors"
      :duration="4000"
      :particleCount="300"
      :force="0.8"
      :stageHeight="viewPortHeight"
      :stageWidth="viewPortWidth"
      class="confetti"
    />
  </div>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/breakpoints.pcss";

.confetti {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 10%;
  left: 50%;
  scrollbar-width: none;
}

.subscription-success {
  position: fixed;
  top: 2rem;
  left: 50%;
  transform: translateX(-50%);
  min-width: 26rem;
  height: fit-content;
  background-color: var(--color-green-50);
  color: var(--color-green-900);
  z-index: 100;
  text-align: center;
  z-index: 9999;
  h4 {
    margin: 0.5rem;
  }
}

.logging-in {
  margin: 5rem auto;
  width: 100%;
  padding-inline: 2rem;
  text-align: center;

  @mixin screen sm {
    margin: 15rem auto;
  }

  p {
    margin: auto;
    margin-top: 1.25rem;
    color: var(--color-rose-500);
  }

  img {
    max-width: 25rem;
    margin: auto;
  }
}
</style>
